/**
 * API Configuration
 *
 * Automatically detects the correct API URL based on environment:
 * - Local development: http://localhost:3000/api (via Vite proxy)
 * - Firebase Hosting (production/staging/custom domains): Same-origin via rewrites
 * - Cloudflare Workers: https://zh-corp-tax-api-staging.workers.dev/api (staging)
 */

/**
 * Get the API base URL based on current environment
 */
export function getApiUrl(): string {
  // Check for explicit override in environment variables
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }

  // Detect environment from hostname
  const hostname = window.location.hostname;

  // Firebase Hosting (Production/Staging) - Use same-origin API via rewrites
  if (
    hostname === "tax.habitus.net" ||
    hostname === "staging.tax.habitus.net" ||
    hostname.includes("zh-corp-tax-wiz.web.app") ||
    hostname.includes("zh-corp-tax-wiz.firebaseapp.com") ||
    hostname.includes("zh-corp-tax-wiz-staging.web.app") ||
    hostname.includes("zh-corp-tax-wiz-staging.firebaseapp.com")
  ) {
    return "/api";
  }

  // Cloudflare Workers Sites
  if (hostname.endsWith(".workers.dev")) {
    return window.location.origin + "/api";
  }

  // Cloudflare Pages deployments
  if (hostname.endsWith(".pages.dev")) {
    if (hostname === "zh-corp-tax-web.pages.dev") {
      return "https://zh-corp-tax-api.habitus.workers.dev/api";
    }
    return "https://zh-corp-tax-api-staging.workers.dev/api";
  }

  // Local development
  if (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname.includes("manus")
  ) {
    return window.location.origin + "/api";
  }

  // Default fallback
  return window.location.origin + "/api";
}

/**
 * Get the tRPC endpoint URL
 */
export function getTrpcUrl(): string {
  const apiUrl = getApiUrl();
  const hostname = window.location.hostname;

  // If the API URL is just "/api", we are using Hosting Rewrites.
  // The function is mounted at /api, so the tRPC root is /api.
  // We do NOT append /trpc here because the Hosting Rewrite /api/** -> Function
  // and the Function maps / -> Router.
  if (apiUrl === "/api") {
    return "/api";
  }

  // For Cloudflare/Local/Others that return .../api
  // We typically append /trpc unless the router is mounted at /api directly.
  // In this project, it seems we standardized on /api as the tRPC root.
  // If getApiUrl returns .../api, we likely just return that.

  if (apiUrl.endsWith("/api")) {
    return apiUrl;
  }

  return apiUrl + "/trpc";
}

/**
 * Check if running in production environment
 */
export function isProduction(): boolean {
  const hostname = window.location.hostname;
  return (
    import.meta.env.MODE === "production" &&
    (hostname === "tax.habitus.net" || hostname === "zh-corp-tax-wiz.web.app")
  );
}

/**
 * Check if running in running in staging environment
 */
export function isStaging(): boolean {
  const hostname = window.location.hostname;
  return (
    hostname === "staging.tax.habitus.net" ||
    hostname.includes("zh-corp-tax-wiz-staging") ||
    hostname.endsWith(".pages.dev")
  );
}

/**
 * Check if running in local development
 */
export function isDevelopment(): boolean {
  const hostname = window.location.hostname;
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname.includes("manus")
  );
}

/**
 * Get environment name
 */
export function getEnvironment(): "production" | "staging" | "development" {
  if (isProduction()) return "production";
  if (isStaging()) return "staging";
  return "development";
}

/**
 * API configuration object
 */
export const apiConfig = {
  baseUrl: getApiUrl(),
  trpcUrl: getTrpcUrl(),
  environment: getEnvironment(),
  isProduction: isProduction(),
  isStaging: isStaging(),
  isDevelopment: isDevelopment(),
} as const;

// Log API configuration in development
if (isDevelopment()) {
  console.log("[API Config]", apiConfig);
}
